/* Imports */
import axios from 'axios';
/* Helpers */
import {
  filterProductByGroup,
  updateProductPageUrls,
} from '../utils/mainUtils.js';
import router from '../router/router.js';

const ITEMS_PER_PAGE = 18;

export default {
  state: {
    autocompleteProductList: [],
    autocompleteProductListError: false,
    products: [],
    productsAndStickyCards: [],
    stickyCards: [],
    metaData: {},
    currentPage: 1,
    displayGroup: true,
    swatches: [],
  },
  getters: {
    getAutocompleteProductList: (state) => {
      const queryParams = router?.currentRoute?.value?.query || {};

      // Normalize compareProducts params
      let compareProducts;

      if (Array.isArray(queryParams.compareProduct)) {
        compareProducts = queryParams.compareProduct;
      } else if (queryParams.compareProduct) {
        compareProducts = [queryParams.compareProduct];
      } else {
        compareProducts = [];
      }

      // Filter out products already selected to prevent duplicates in Autocomplete
      return state.autocompleteProductList.filter((item) => !compareProducts.includes(item.value?.productId));
    },
    getAutocompleteProductListError: (state) => state.autocompleteProductListError,
    getProducts: (state, getters) => filterProductByGroup(state.products, state.displayGroup, getters.getHasAppliedFilters),
    getProductsAndStickyCards: (state, getters) => {
      let cards = state.productsAndStickyCards;
      // Do not display sticky cards if compare is active
      if (getters.getCompareActiveState) {
        cards = cards.filter((card) => card?.sys?.contentType?.sys?.id !== 'stickyCustomCard');
      }

      const startIndex = (getters.getCurrentPage - 1) * getters.getMetaData.itemsPerPage;
      const endIndex = getters.getCurrentPage * getters.getMetaData.itemsPerPage;
      return cards.slice(startIndex, endIndex);
    },
    getProductsByGroupId: (state) => (groupId) => state.products.filter((product) => product.groupId === groupId),
    getStickyCards: (state) => state.stickyCards,
    getMetaData: (state) => state.metaData,
    getCurrentPage: (state) => state.currentPage,
    getDisplayGroup: (state) => state.displayGroup,
    getSwatches: (state) => state.swatches,
    getProductSwatches: (state) => (pid) => state.swatches.find((item) => item.productId === pid)?.swatches || [],
    getSeriesSwatches: (state) => (groupId) => state.swatches.filter((item) => item.groupId === groupId)
      .map((item) => item.swatches)
      .flat(),
  },
  mutations: {
    setAutocompleteProductList: (state, list) => {
      const formattedList = [];
      if (Array.isArray(list)) {
        list.forEach((item) => {
          let partNumber = false;

          if (item.partNumber) {
            partNumber = item.partNumber;
          }

          const shouldAddItemToList = item.productName && item.productId;

          if (shouldAddItemToList) {
            const product = {
              label: item.productName,
              value: {
                productId: item.productId,
                partNumber,
              },
            };

            formattedList.push(product);
          }
        });
      }

      // eslint-disable-next-line no-param-reassign
      state.autocompleteProductList = formattedList;
    },
    setProducts: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.products = value;
    },
    setProductsAndStickyCards: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.productsAndStickyCards = value;
    },
    setMetaData: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.metaData = value;
    },
    setStickyCards: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.stickyCards = value;
    },
    setDisplayGroup: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.displayGroup = value;
    },
    setCurrentPage: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.currentPage = value;
    },
    setAutocompleteProductListError: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.autocompleteProductListError = value;
    },
    setSwatches: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.swatches = value;
    },
  },
  actions: {
    loadProducts: async ({ commit, getters, dispatch }) => {
      const filteredQueryParams = Object.entries(getters.getQueryParams)
        .filter(([key]) => ['sorter', 'series'].includes(key)
          || /^[A-Z][A-Z0-9_]+$/.test(key)
          || /^productFilter.+$/.test(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      const queryObject = {
        ...filteredQueryParams,
        cg: getters.getCustomerGroups?.split('|')?.join('_') || '',
        categoryKey: getters.getCategoryKey.trim(),
        locale: getters.getLocale,
        storeCode: getters.getStoreCode,
        appName: 'www-category-pages',
      };

      dispatch('setIsLoading', true);
      const queryString = new URLSearchParams(queryObject).toString();

      // TODO: Remove conditional once NETFE-19709 is released and ITFE_CATEGORY_PAGES_ENABLE_NEW_FILTERS_API flag is deleted
      const urlPath = getters.getContentfulEnvSettings?.ITFE_CATEGORY_PAGES_ENABLE_NEW_FILTERS_API ? 'getCategoryProducts' : 'getProducts';
      const url = `${window?.location?.origin}${getters.getPublicPath}/api/${urlPath}?${queryString}`;

      try {
        const response = await axios.get(url);

        if (response.status !== 200) {
          throw new Error(`Received ${response.status}: ${response.statusText}`);
        }

        const updatedProducts = updateProductPageUrls(response.data?.products ?? []);
        // Adjust meta data based on products shown
        const productsDisplayed = filterProductByGroup(updatedProducts, getters.getDisplayGroup);
        const metaData = response.data.meta;
        metaData.totalPages = Math.max(1, Math.ceil(productsDisplayed.length / metaData.itemsPerPage));

        if (response?.data?.meta?.currentPage > metaData.totalPages) {
          dispatch('setQueryParams', {
            ...getters.getQueryParams,
            currentPage: metaData.totalPages,
          });
        }
        const { products } = response.data;

        // Try fetching color swatches, but don’t block the 'loadProducts' call if it fails
        try {
          await dispatch('fetchColorSwatches', { products });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch color swatches:', error?.message);
        }

        // set products (this will contain all products for all pages)
        commit('setProducts', products);

        // set meta object data
        commit('setMetaData', metaData);

        dispatch('mergeProductsWithStickyCards');
        dispatch('fetchPrices');
      } catch (error) {
        commit('setProducts', []);
        commit('setMetaData', {
          totalProductCount: 0,
          currentPage: 1,
          totalPages: 1,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        dispatch('mergeProductsWithStickyCards');
        // eslint-disable-next-line no-console
        console.error('Failed to load products:', error?.message);
        throw new Error(`Failed to load products: ${error?.message}`, { cause: error });
      } finally {
        dispatch('setIsLoading', false);
      }
    },
    mergeProductsWithStickyCards: ({ commit, getters }) => {
      const productCards = getters.getProducts;
      const { itemsPerPage } = getters.getMetaData;
      const stickyCards = getters.getStickyCards || [];
      let currentPageNumber = 1;
      const cards = [];
      let offset = 0;

      const stickyCardsOnPage = (pageNumber) => stickyCards
        .filter((card) => {
          if (getters.getHasAppliedFilters && card?.sys?.contentType?.sys?.id === 'stickyCustomCard' && !card?.fields?.showWhenFiltered) return false;
          return !card?.fields?.displayOnPages || card?.fields?.displayOnPages.includes(String(pageNumber));
        })
        .sort((a, b) => a.fields.placementIndex - b.fields.placementIndex);

      while (offset < productCards.length) {
        const pageNumber = currentPageNumber.toString();
        // Filter out sticky cards that should be shown on the current page
        const currentPageStickyCards = stickyCardsOnPage(pageNumber);
        const numberOfProductCards = itemsPerPage - currentPageStickyCards.length;
        const currentPageCards = numberOfProductCards > 0
          ? productCards.slice(offset, offset + numberOfProductCards)
          : [];
        currentPageStickyCards
          .forEach((card) => currentPageCards.splice(card.fields.placementIndex - 1, 0, card));
        cards.push(...currentPageCards);
        currentPageNumber += 1;
        if (numberOfProductCards <= 0) break;
        offset += numberOfProductCards;
      }
      commit('setProductsAndStickyCards', cards.length ? cards : stickyCardsOnPage(1));
      commit('setMetaData', { ...getters.getMetaData, totalPages: currentPageNumber - 1 || 1 });
    },
    // The products API no longer honors the price sorter query params,
    // so sorting by price is done client-side once all prices have been fetched
    sortProductsByPrice: ({
      commit,
      dispatch,
      getters,
      state,
    }) => {
      const sorter = getters.getQueryParams?.sorter;
      if (!['price-asc', 'price-desc'].includes(sorter)) return;

      const getSortPrice = (product) => {
        const unformatted = getters.getPriceByPid(product?.id)?.unformatted;
        return unformatted?.salePrice ?? unformatted?.listPrice ?? null;
      };

      const sortedProducts = [...state.products].sort((productA, productB) => {
        const priceA = getSortPrice(productA);
        const priceB = getSortPrice(productB);
        // Products with failed or empty prices go to the end of the list
        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return sorter === 'price-asc' ? priceA - priceB : priceB - priceA;
      });

      commit('setProducts', sortedProducts);
      dispatch('mergeProductsWithStickyCards');
    },
    fetchColorSwatches: async ({ commit, dispatch }, payload) => {
      const { products } = payload;
      const productsOnly = products.filter((product) => !product.group);

      // Parsing only product-level data
      const swatchesPromises = productsOnly.map(async (product) => ({
        groupId: product.groupId,
        productId: product.id,
        swatches: await dispatch('loadColorSwatches', { id: product.id }),
      }));

      // Make a single array of all product swatches, breaking down groups
      const results = await Promise.allSettled(swatchesPromises);
      const swatches = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

      commit('setSwatches', swatches);
    },
    loadAutocompleteProductList: async ({ getters, commit }) => {
      // If we already have called and populated the autocompleteProductList getter, return early
      if ((getters.autocompleteProductList || []).length) return;

      try {
        const url = `${window?.location?.origin}${getters.getPublicPath}/api/getDisplayableProducts?locale=${getters.getLocale}`;
        const response = await axios.get(url);

        if (response.status !== 200) {
          throw new Error(`Received ${response.status}: ${response.statusText}`);
        }

        // set master product list (this will contain all products at Garmin)
        commit('setAutocompleteProductList', response?.data?.data || []);
        // eslint-disable-next-line no-unused-vars
      } catch (error) {
        commit('setAutocompleteProductListError', true);
      }
    },
    loadProductDetailsByPID: async ({ getters }, payload) => {
      const url = `${window?.location?.origin}${getters.getPublicPath}/api/getProductDetailsByPID`
        + `?pid=${payload}&locale=${getters.getLocale}`;
      const response = await axios.get(url);

      if (response.status !== 200) {
        throw new Error(`Received ${response.status}: ${response.statusText}`);
      }

      return response?.data?.data;
    },
    loadProductImageDetailsByPID: async ({ getters }, payload) => {
      const url = `${window?.location?.origin}${getters.getPublicPath}/api/getProductImageByPID`
        + `?pid=${payload}&locale=${getters.getLocale}`;
      const response = await axios.get(url);

      if (response.status !== 200) {
        throw new Error(`Received ${response.status}: ${response.statusText}`);
      }

      return response?.data?.data;
    },
    loadColorSwatches: async ({ getters }, payload) => {
      const locale = getters?.getLocale;
      const id = payload?.id;
      const url = `${window?.location?.origin}${getters.getPublicPath}/api/getColorSwatches?locale=${locale}&id=${id}`;
      const response = await axios.get(url);

      if (response.status !== 200) {
        throw new Error(`Received ${response.status}: ${response.statusText}`);
      }

      return response?.data?.data;
    },
    // this should be false only if filters are applied
    loadDisplayGroup: ({ commit }, group) => {
      commit('setDisplayGroup', group);
    },
    loadStickyCards: ({ commit }, payload) => {
      const { stickyCardGrid } = payload || {};
      const stickyCardFromGrid = Object.entries(stickyCardGrid?.fields || {})
        ?.filter(([key, value]) => key.startsWith('cardPosition') && value)
        .map(([key, value]) => ({
          ...value,
          fields: {
            ...value.fields,
            placementIndex: parseInt(key.replace('cardPosition', ''), 10),
          },
        })) || [];

      // Filter out all 'final cards' from the sticky card grid and assign placementIndex accordingly
      let placementIndex = 18;
      const finalCards = Object.entries(stickyCardGrid?.fields || {})
        ?.find(([key, value]) => key === 'finalCards' && value)?.[1]
        ?.map((card) => {
          placementIndex += 1;
          return {
            ...card,
            fields: {
              ...card.fields,
              placementIndex,
            },
          };
        }) || [];

      const stickyCardResult = stickyCardFromGrid.length
        ? stickyCardFromGrid.concat(finalCards)
        : payload?.stickyCustomCard || [];
      // In case there are duplicate cards with the same placement index, we only want to keep the first occurrence
      const uniqueStickyCards = stickyCardResult.filter((card, index, self) => index === self.findIndex(
        (el) => el?.fields?.placementIndex === card?.fields?.placementIndex,
      ));
      commit('setStickyCards', uniqueStickyCards);
    },
    loadCurrentPage: ({ getters, dispatch }, currentPageOpts) => {
      dispatch('setQueryParams', {
        ...getters.getQueryParams,
        ...currentPageOpts,
      });
    },
  },
};
